import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Bus, Route, School, Users, UserRound, Sparkles } from "lucide-react";
import { useData } from "../contexts/DataContext";
import { MapPanel } from "../components/MapPanel";
import { StatCard } from "../components/StatCard";
import { formatDateTime, getStatusLabel } from "../lib/utils";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { overview, fleetLocations, loadOverview, loadFleetLocations } = useData();

  useEffect(() => {
    const hydrate = async () => {
      await Promise.all([loadOverview(), loadFleetLocations()]);
    };
    hydrate();
    const interval = window.setInterval(hydrate, 10000);
    return () => window.clearInterval(interval);
  }, [loadOverview, loadFleetLocations]);

  const markers = useMemo(
    () =>
      fleetLocations.map((location) => ({
        id: location.trip_id,
        latitude: location.latitude,
        longitude: location.longitude,
        label: `${location.vehicle_registration || "Vehicle"} • ${location.child_name || "Trip"}`,
      })),
    [fleetLocations]
  );

  const stats = overview?.stats || {};
  const activeTrips = overview?.active_trips || [];
  const incidents = overview?.recent_incidents || [];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="guardian-card bg-gradient-to-r from-[#163D6B] to-[#1E4976] p-6 text-white">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
              <Sparkles size={24} />
            </div>
            <div>
              <h2 className="font-['Manrope'] text-xl font-semibold">Quick Start: Client Setup Wizard</h2>
              <p className="mt-1 text-sm text-white/80">
                Onboard a new client end-to-end in 7 simple steps: school, students, parents, driver, vehicle, and first trip.
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate("/admin/setup-wizard")}
            className="shrink-0 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-[#163D6B] hover:bg-white/90"
          >
            Launch Wizard
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Active trips" value={stats.active_trips || 0} icon={Route} accent="blue" helper="Trips currently reporting progress" />
        <StatCard label="Fleet vehicles" value={stats.vehicles || 0} icon={Bus} accent="emerald" helper="Tracked vehicles across the system" />
        <StatCard label="Open incidents" value={stats.open_incidents || 0} icon={AlertTriangle} accent="rose" helper="Issues requiring operational review" />
        <StatCard label="Total users" value={stats.users || 0} icon={Users} accent="amber" helper="Admins, drivers, schools, and parents" />
      </div>

      <div className="guardian-card bg-white p-6">
        <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Today's Performance</h2>
        <p className="mt-1 text-sm text-[#64748B]">Key metrics for {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} (midnight-to-midnight UTC)</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Trips Today</p>
            <p className="mt-2 text-3xl font-semibold text-[#0F172A]">{stats.trips_today || 0}</p>
            <p className="mt-1 text-xs text-[#64748B]">Created since midnight</p>
          </div>
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Active Drivers</p>
            <p className="mt-2 text-3xl font-semibold text-[#0F172A]">{stats.drivers || 0}</p>
            <p className="mt-1 text-xs text-[#64748B]">Currently active</p>
          </div>
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Completed Today</p>
            <p className="mt-2 text-3xl font-semibold text-[#0F172A]">{stats.completed_today || 0}</p>
            <p className="mt-1 text-xs text-[#64748B]">Trips finished today</p>
          </div>
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
            <p className="text-sm text-[#64748B]">Incidents</p>
            <p className="mt-2 text-3xl font-semibold text-[#0F172A]">{stats.open_incidents || 0}</p>
            <p className="mt-1 text-xs text-[#64748B]">Open & investigating</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
        <div className="guardian-card bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Live fleet map</h2>
              <p className="text-sm text-[#64748B]">GPS locations tied to active trips. Google Maps activates automatically when the key is configured.</p>
            </div>
          </div>
          <MapPanel markers={markers} height={420} title="Live fleet locations" />
        </div>

        <div className="space-y-6">
          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Current operational mix</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-sm text-[#64748B]">Drivers</p>
                <p className="mt-1 text-2xl font-semibold text-[#0F172A]">{stats.drivers || 0}</p>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-sm text-[#64748B]">Schools</p>
                <p className="mt-1 text-2xl font-semibold text-[#0F172A]">{stats.schools || 0}</p>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-sm text-[#64748B]">Parents</p>
                <p className="mt-1 text-2xl font-semibold text-[#0F172A]">{stats.parents || 0}</p>
              </div>
              <div className="rounded-2xl bg-[#F8FAFC] p-4">
                <p className="text-sm text-[#64748B]">Trips</p>
                <p className="mt-1 text-2xl font-semibold text-[#0F172A]">{stats.trips || 0}</p>
              </div>
            </div>
          </div>

          <div className="guardian-card bg-white p-6">
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Recent incidents</h2>
            <div className="mt-4 space-y-3">
              {incidents.length ? (
                incidents.map((incident) => (
                  <div key={incident.id} className="rounded-2xl border border-[#E2E8F0] p-4">
                    <div className="flex items-center justify-between gap-4">
                      <p className="font-medium text-[#0F172A]">{incident.description}</p>
                      <span className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rose-700">
                        {incident.severity}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#64748B]">{formatDateTime(incident.timestamp)}</p>
                  </div>
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-[#CBD5E1] p-4 text-sm text-[#64748B]">No incidents recorded yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Active trips</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">Child</th>
                <th className="px-6 py-3 font-medium">Driver</th>
                <th className="px-6 py-3 font-medium">Vehicle</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">ETA</th>
                <th className="px-6 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {activeTrips.length ? (
                activeTrips.map((trip) => (
                  <tr key={trip.id} className="border-t border-[#E2E8F0]">
                    <td className="px-6 py-4 font-medium text-[#0F172A]">{trip.child_name}</td>
                    <td className="px-6 py-4 text-[#475569]">{trip.driver_name || "Unassigned"}</td>
                    <td className="px-6 py-4 text-[#475569]">{trip.vehicle_registration || "Unassigned"}</td>
                    <td className="px-6 py-4 text-[#475569]">{getStatusLabel(trip.status)}</td>
                    <td className="px-6 py-4 text-[#475569]">{trip.eta_minutes ? `${trip.eta_minutes} min` : "Pending"}</td>
                    <td className="px-6 py-4 text-[#475569]">{formatDateTime(trip.last_location?.timestamp || trip.updated_at)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-[#64748B]">No active trips at the moment.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
